// ─── Error Code Catalog ───────────────────────────────────────────────────────
// Keep this in sync with src/utils/error-codes.ts on the backend.

export const ApiErrorCode = {
  // Validation (HTTP 400)
  VALIDATION_FAILED:         'VALIDATION_FAILED',
  INVALID_INPUT:             'INVALID_INPUT',
  CAST_ERROR:                'CAST_ERROR',

  // Authentication (HTTP 401)
  UNAUTHORIZED:              'UNAUTHORIZED',
  TOKEN_EXPIRED:             'TOKEN_EXPIRED',
  TOKEN_INVALID:             'TOKEN_INVALID',
  TOKEN_REVOKED:             'TOKEN_REVOKED',
  SESSION_EXPIRED:           'SESSION_EXPIRED',

  // Authorization (HTTP 403)
  FORBIDDEN:                 'FORBIDDEN',
  INSUFFICIENT_PERMISSIONS:  'INSUFFICIENT_PERMISSIONS',
  INSUFFICIENT_SCOPE:        'INSUFFICIENT_SCOPE',
  ACCOUNT_DISABLED:          'ACCOUNT_DISABLED',

  // Resource (HTTP 404 / 409)
  NOT_FOUND:                 'NOT_FOUND',
  CONFLICT:                  'CONFLICT',

  // Business Rule (HTTP 422)
  BUSINESS_RULE_VIOLATION:   'BUSINESS_RULE_VIOLATION',

  // Infrastructure (HTTP 429 / 500)
  RATE_LIMIT_EXCEEDED:       'RATE_LIMIT_EXCEEDED',
  INTERNAL_ERROR:            'INTERNAL_ERROR',
} as const;

export type ApiErrorCodeType = typeof ApiErrorCode[keyof typeof ApiErrorCode];

// ─── Validation error item ────────────────────────────────────────────────────

export interface ValidationError {
  /** The form field / request body key that failed validation */
  field: string;
  /** Human-readable validation message — safe to display next to the field */
  message: string;
}

// ─── Standard error envelope ─────────────────────────────────────────────────

export interface ApiErrorResponse {
  success: false;
  message: string;
  code: ApiErrorCodeType;
  statusCode: number;
  requestId: string;
  timestamp: string;
  /** Present only when code === 'VALIDATION_FAILED' */
  validationErrors?: ValidationError[];
  /** Present for CONFLICT and BUSINESS_RULE_VIOLATION errors */
  details?: Record<string, unknown>;
  /** Present only for RATE_LIMIT_EXCEEDED — seconds until window resets */
  retryAfter?: number;
}

// ─── Success envelope (for reference) ────────────────────────────────────────

export interface ApiSuccessResponse<T = unknown> {
  success: true;
  data?: T;
  message?: string;
  count?: number;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export type ApiUnionResponse<T = unknown> = ApiSuccessResponse<T> | ApiErrorResponse;
